// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy } from "@angular/core";
import { BehaviorSubject, ReplaySubject, Subject, map, switchMap, takeUntil, tap } from "rxjs";

import { JslibModule } from "@vowkey/angular/jslib.module";
import { Account } from "@vowkey/common/auth/abstractions/account.service";
import { I18nService } from "@vowkey/common/platform/abstractions/i18n.service";
import { LogService } from "@vowkey/common/platform/abstractions/log.service";
import {
  SemanticLogger,
  disabledSemanticLoggerProvider,
  ifEnabledSemanticLoggerProvider,
} from "@vowkey/common/tools/log";
import { UserId } from "@vowkey/common/types/guid";
import { ColorPasswordModule, IconButtonModule, ItemModule } from "@vowkey/components";
import {
  AlgorithmsByType,
  CredentialAlgorithm,
  CredentialGeneratorService,
  resolveGeneratorAccount,
} from "@vowkey/generator-core";
import { GeneratedCredential, GeneratorHistoryService } from "@vowkey/generator-history";

import { PIN_GENERATOR_MODE } from "./pin-and-strength";
import { translate } from "./util";

// FIXME(https://bitwarden.atlassian.net/browse/CL-764): Migrate to OnPush
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: "bit-credential-generator-history",
  templateUrl: "credential-generator-history.component.html",
  imports: [CommonModule, ColorPasswordModule, IconButtonModule, JslibModule, ItemModule],
})
export class CredentialGeneratorHistoryComponent implements OnChanges, OnInit, OnDestroy {
  private readonly destroyed = new Subject<void>();
  protected readonly credentials$ = new BehaviorSubject<GeneratedCredential[]>([]);

  constructor(
    private generatorService: CredentialGeneratorService,
    private history: GeneratorHistoryService,
    private i18nService: I18nService,
    private logService: LogService,
  ) {}

  // FIXME(https://bitwarden.atlassian.net/browse/CL-903): Migrate to Signals
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  account: Account | null = null;

  protected account$ = new ReplaySubject<Account>(1);

  /** Send structured debug logs from the credential generator component
   *  to the debugger console.
   *
   *  @warning this may reveal sensitive information in plaintext.
   */
  // FIXME(https://bitwarden.atlassian.net/browse/CL-903): Migrate to Signals
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  debug: boolean = false;

  // this `log` initializer is overridden in `ngOnInit`
  private log: SemanticLogger = disabledSemanticLoggerProvider({});

  async ngOnChanges(changes: SimpleChanges) {
    const account = changes?.account;
    if (account) {
      const resolved = resolveGeneratorAccount(account.currentValue ?? this.account);
      this.log.debug(
        {
          previousUserId: account?.previousValue?.id as UserId,
          currentUserId: resolved.id,
        },
        "account input change detected",
      );
      this.account$.next(resolved);
    }
  }

  ngOnInit() {
    this.log = ifEnabledSemanticLoggerProvider(this.debug, this.logService, {
      type: "CredentialGeneratorComponent",
    });
    if (!this.account) {
      this.account$.next(resolveGeneratorAccount(null));
    }

    this.account$
      .pipe(
        tap((account) => this.log.info({ accountId: account.id }, "loading credential history")),
        switchMap((account) => this.history.credentials$(account.id)),
        map((credentials) => credentials.filter((c) => (c.credential ?? "") !== "")),
        takeUntil(this.destroyed),
      )
      .subscribe(this.credentials$);
  }

  protected getCopyText(credential: GeneratedCredential) {
    if (credential.algorithm === PIN_GENERATOR_MODE) {
      const t = this.i18nService.t("vkCopyPin");
      return t && t !== "vkCopyPin" ? t : "Copy PIN";
    }
    const info = this.generatorService.algorithm(this.algorithmId(credential));
    return translate(info.i18nKeys.copyCredential, this.i18nService);
  }

  protected getGeneratedValueText(credential: GeneratedCredential) {
    if (credential.algorithm === PIN_GENERATOR_MODE) {
      const t = this.i18nService.t("vkPin");
      return t && t !== "vkPin" ? t : "PIN";
    }
    const info = this.generatorService.algorithm(this.algorithmId(credential));
    return translate(info.i18nKeys.credentialType, this.i18nService);
  }

  // Prefer the algorithm captured at generation time so passwords and passphrases
  //   resolve to their respective i18n keys. Fall back to the first algorithm for
  //   the category for credentials persisted before the algorithm was tracked.
  private algorithmId(credential: GeneratedCredential): CredentialAlgorithm {
    if (credential.algorithm) {
      return credential.algorithm as CredentialAlgorithm;
    }
    const [id] = AlgorithmsByType[credential.category];
    return id;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();

    this.log.debug("component destroyed");
  }
}
